import { Tooltip, Typography } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

import { useAssessmentProgressOptional } from '~/client/components/assessment/assessment-progress-context';
import { getDesignTokens } from '~/client/components/base/core/color';
import Text from '~/client/components/base/text';
import { useAssessmentFromId } from '~/client/hooks/assessment';
import * as GQL from '~/graphql/generated/client';
import Arranger from '~c/components/base/arranger';

interface Props {
  className?: string;
  controlBundle: GQL.ControlBundleFragment;
  isActive?: boolean;
}

const LeftNavBundleItemImpl: React.FC<Props> = ({
  className,
  controlBundle,
  isActive,
}) => {
  const {
    projectId,
    candidateId,
    assessmentId,
    functionalModelId,
    versionId,
    agentId,
  } = useParams();
  const progressContext = useAssessmentProgressOptional();
  const effectiveProjectId =
    candidateId ?? agentId ?? functionalModelId ?? projectId;
  const { bundleHelper } = useAssessmentFromId(
    effectiveProjectId,
    assessmentId
  );
  const { controlCount, answerCount, answerBundle } = bundleHelper(
    controlBundle._id,
    versionId ?? ''
  );

  // Get live bundle progress from context, fallback to cache value
  const liveBundleProgress = answerBundle?._id
    ? progressContext?.getBundleProgress(answerBundle._id)
    : undefined;
  const displayAnswerCount = liveBundleProgress?.numAnswers ?? answerCount;
  const displayControlCount = liveBundleProgress?.numControls ?? controlCount;

  // Reference to check if text is truncated
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };
    checkTruncation();
    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [controlBundle.name]);

  return (
    <Arranger className={`${className} ${isActive ? 'active' : ''}`}>
      <Tooltip
        title={controlBundle.name}
        placement="top"
        arrow
        disableHoverListener={!isTruncated}
      >
        <Typography
          className="bundle-name"
          noWrap
          align="left"
          ref={textRef}
          component="span"
        >
          {controlBundle.name}
        </Typography>
      </Tooltip>
      <Text className="progress-count">
        {displayAnswerCount}/{displayControlCount}
      </Text>
    </Arranger>
  );
};

const LeftNavBundleItem = muiStyled(LeftNavBundleItemImpl)(({ theme }) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );
  return css`
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: 8px;

    .bundle-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1 1 auto;
      min-width: 0;
      color: ${t.nav.itemTextOnDark};
    }

    .progress-count {
      flex: 0 0 auto;
      white-space: nowrap;
      color: ${t.nav.itemTextOnDark};
      font-size: 14px;
    }

    .nav-badge {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 0px 12px;
      width: 44px;
      height: 20px;
      background: ${theme.palette.action.hover};
      border-radius: ${theme.shape.borderRadius}px;
      flex: none;
      order: 1;
      flex-grow: 0;
      color: ${t.nav.itemTextOnDark};
    }

    &.active {
      .bundle-name {
        color: ${theme.palette.primary.contrastText};
      }

      .progress-count {
        color: ${theme.palette.primary.contrastText};
      }

      .nav-badge {
        background: ${theme.palette.primary.main};
        color: ${theme.palette.primary.contrastText};
      }
    }
  `;
});

export default LeftNavBundleItem;
